import { parseSchemaRef, type IngestEnvelope, type SchemaRef } from '@collector/shared'

/**
 * Narrow-typed projection of the `tccc_card` payload (v1 and v2). See
 * `collector/schemas/tccc_card.v*.json` for the source of truth. Every field
 * is optional because an in-field card can be partially filled; the renderer
 * degrades gracefully.
 */
export interface TcccPayload {
  capturedAt?: string
  demographics?: {
    battleRoster?: string
    age?: string
    height?: string
    weight?: string
    gender?: 'MALE' | 'FEMALE' | ''
    bloodType?: string
    allergies?: string[]
    evacPriority?: 'A' | 'B' | 'C' | 'D' | ''
    lastName?: string
    patientId?: string
  }
  assessment?: {
    avpu?: 'A' | 'V' | 'P' | 'U'
    pain?: number
  }
  injuriesFront?: TcccInjury[]
  injuriesBack?: TcccInjury[]
  tourniquets?: TcccTourniquet[]
  treatments?: {
    tq?: string[]
    dressing?: string[]
    airway?: string[]
    breathing?: string[]
    otherTreatments?: string[]
  }
  medications?: {
    analgesics?: TcccMedication[]
    antibiotics?: TcccMedication[]
    ivFluids?: TcccMedication[]
    plasmaFluids?: TcccMedication[]
    txaFluids?: TcccMedication[]
    bloodFluids?: TcccMedication[]
  }
  vitalSigns?: TcccVitals[]
  responder?: { name?: string; battleRoster?: string }
  notes?: string
}

export interface TcccInjury {
  type: string
  points: { x: number; y: number }[]
}

export interface TcccTourniquet {
  x: number
  y: number
  type: string
  createdAt: string
  active: boolean
  updated?: boolean
}

export interface TcccMedication {
  name: string
  form: string
  dosage?: string
  time: string
}

export interface TcccVitals {
  time: string
  pulse?: number
  systolicBP?: number
  diastolicBP?: number
  respirationRate?: number
  oxygenSaturation?: number
  bloodLossDynamics?: number
  shockIndex?: number
}

export const TCCC_SCHEMA_NAME = 'tccc_card'

/** True for any version of the TCCC card schema. Matches `tccc_card@N`. */
export function isTcccSchema(ref: SchemaRef): boolean {
  try {
    return parseSchemaRef(ref).name === TCCC_SCHEMA_NAME
  } catch (_err) {
    // A malformed schema ref simply isn't TCCC. The ref regex is enforced at
    // the API boundary, so we hit this only for edge/test inputs.
    return false
  }
}

/**
 * Convert a v2 flat-interleaved coordinate array `[x1,y1,x2,y2,…]` into the
 * v1 structured form `[{x,y},…]`. If the array already contains objects it is
 * returned unchanged so the function is safe to call on either version.
 */
function normalizePoints(points: unknown): { x: number; y: number }[] {
  if (!Array.isArray(points) || points.length === 0) return []
  if (typeof points[0] === 'object') return points as { x: number; y: number }[]
  // Flat interleaved integers
  const out: { x: number; y: number }[] = []
  for (let i = 0; i + 1 < points.length; i += 2) {
    out.push({ x: points[i] as number, y: points[i + 1] as number })
  }
  return out
}

function normalizeInjuries(injuries: unknown): TcccInjury[] | undefined {
  if (!Array.isArray(injuries)) return undefined
  return (injuries as TcccInjury[]).map((inj) => ({
    ...inj,
    points: normalizePoints(inj.points),
  }))
}

/**
 * Coerce an opaque payload into a `TcccPayload`, normalising v2 flat
 * coordinate arrays into the v1 structured `{x,y}` form so downstream
 * code can always use the same shape.
 */
export function asTcccPayload(data: Record<string, unknown>): TcccPayload {
  const payload = data as TcccPayload
  if (payload.injuriesFront) payload.injuriesFront = normalizeInjuries(payload.injuriesFront)
  if (payload.injuriesBack) payload.injuriesBack = normalizeInjuries(payload.injuriesBack)
  return payload
}

/** Known vitals measurement keys, in display order. */
export const VITALS_KEYS = [
  'pulse',
  'systolicBP',
  'diastolicBP',
  'respirationRate',
  'oxygenSaturation',
  'shockIndex',
  'bloodLossDynamics',
] as const
export type VitalsKey = (typeof VITALS_KEYS)[number]

/**
 * Pick the most recent non-null value per measurement across a vitals
 * series, stamped with the latest time any of the reduced values was
 * observed. A field missing from every row is omitted from the result.
 *
 * Returns null only when the series has no usable readings at all.
 */
export function latestVitalsSnapshot(series: TcccVitals[] | undefined): TcccVitals | null {
  if (!series || series.length === 0) return null
  // Sort by time ascending so that iterating from the end finds the freshest
  // reading per field. Copy first — never mutate the caller's array. Compare
  // parsed timestamps rather than strings: payloads can mix `…T18:04Z` and
  // `…T18:04:55Z`, and `Z` > `:` would put the shorthand row last.
  const sorted = [...series].sort((a, b) => Date.parse(a.time) - Date.parse(b.time))
  const snapshot: TcccVitals = { time: sorted[sorted.length - 1]!.time }
  let any = false
  for (const key of VITALS_KEYS) {
    for (let i = sorted.length - 1; i >= 0; i--) {
      const v = sorted[i]![key]
      if (typeof v === 'number' && Number.isFinite(v)) {
        snapshot[key] = v
        any = true
        break
      }
    }
  }
  return any ? snapshot : null
}

/**
 * For a TCCC envelope, collapse `data.vitalSigns` to a single snapshot
 * holding only the most recent value per measurement type. Non-TCCC
 * envelopes are returned unchanged so the caller can always use this.
 *
 * The point is to keep the exported QR code small and focused on the
 * handoff-relevant latest numbers, not the full time series.
 */
export function condenseTcccEnvelopeForQr(envelope: IngestEnvelope): IngestEnvelope {
  if (!isTcccSchema(envelope.schema)) return envelope
  const payload = asTcccPayload(envelope.data)
  const snapshot = latestVitalsSnapshot(payload.vitalSigns)
  const nextVitals = snapshot ? [snapshot] : []
  return {
    ...envelope,
    data: {
      ...envelope.data,
      vitalSigns: nextVitals,
    },
  }
}
